import { useRef } from "react";
import { toast } from "sonner";

import { useUpdateActorMutation } from "Actors/Api/Mutations/useUpdateActorMutation";
import { ActorForm, type ActorFormHandle } from "Actors/Forms/ActorForm";
import {
  type ActorFormData,
  toSaveActorRequest,
} from "Actors/Forms/actorFormSchema";
import type { ActorDetailDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";

interface EditActorModalProps {
  isOpen: boolean;
  onClose: () => void;
  actor: ActorDetailDto;
  actorID: string;
}

export const EditActorModal = ({
  isOpen,
  onClose,
  actor,
  actorID,
}: EditActorModalProps): React.ReactNode => {
  const { t } = useResource();
  const formRef = useRef<ActorFormHandle | null>(null);
  const updateActorMutation = useUpdateActorMutation();

  const onSubmit = async (formData: ActorFormData) => {
    try {
      await updateActorMutation.mutateAsync({
        actorID,
        data: toSaveActorRequest(formData),
      });
      toast.success(t(Resources.Actors.EditActorModal.SuccessMessage));
      onClose();
    } catch {
      // field errors shown in form, non-field errors as toast via useHandleServerError
    }
  };

  const handleClose = () => {
    formRef.current?.reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Actors.EditActorModal.Title)}
      onCancel={handleClose}
      onConfirm={() => formRef.current?.submit(onSubmit)()}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Actors.EditActorModal.Save)}
      maxWidth="800px"
    >
      <ActorForm
        actor={actor}
        formRef={formRef}
        serverError={updateActorMutation.error}
      />
    </BlModal>
  );
};
